import { useDidUpdateEffect } from 'client/utils/use-did-update-effect';
import { makeUndoRedo, on } from 'minidoc-editor';
import { useEffect, useMemo, useRef } from 'preact/hooks';

function eql<T extends object>(a: T, b: T) {
  const keys = new Set();
  for (const k in a) {
    if (a[k] !== b[k]) {
      return false;
    }
    keys.add(k);
  }
  for (const k in b) {
    if (!keys.has(k)) {
      return false;
    }
  }
  return true;
}

export function useUndoRedo<T extends object>(state: T, setStateRaw: (state: T) => void) {
  const prevState = useRef(state);
  const setState = useRef(setStateRaw);
  setState.current = setStateRaw;
  const undoRedo = useMemo(() => {
    const result: ReturnType<typeof makeUndoRedo<T>> = makeUndoRedo({
      initialState: state,
      setState(value) {
        prevState.current = value;
        setState.current(value);
      },
    });

    return result;
  }, []);

  useDidUpdateEffect(() => {
    if (eql(state, prevState.current)) {
      return;
    }
    undoRedo.push(state);
  }, [state]);

  useEffect(
    () =>
      on(document, 'keydown', (e: KeyboardEvent) => {
        if (e.defaultPrevented) {
          return;
        }
        const isCtrl = e.metaKey || e.ctrlKey;
        const isRedo = isCtrl && e.shiftKey && e.code === 'KeyZ';
        const isUndo = isCtrl && e.code === 'KeyZ';
        if (!isRedo && !isUndo) {
          return;
        }
        e.preventDefault();
        isRedo ? undoRedo.redo() : undoRedo.undo();
      }),
    [undoRedo],
  );

  return undoRedo;
}
